/**
 * Makes structured subscriber IDs readable in the dashboard.
 *
 * Products that integrate Pingr are encouraged to namespace subscribers so one Pingr environment
 * can serve several products/tenants (see Integration.md):
 *
 *   product:<product>:tenant:<tenantId>:user:<userId>     e.g. HRMS tenant user
 *   product:<product>:platform:user:<userId>              e.g. HRMS platform/superadmin
 *
 * Rendered raw, that is a 90-character opaque string. This parses it into labelled parts so the
 * list shows "hrms · Nimbus People Labs · user 019f6965" instead.
 *
 * Human-friendly names are only shown when the integrating product actually sent them on the
 * subscriber's `data` (e.g. `tenantName`); otherwise we fall back to a shortened id, never a lie.
 */

export type SubscriberIdentity = {
  /** True when the id follows the documented `product:` convention. */
  structured: boolean;
  product?: string;
  /** 'tenant' | 'platform' — the isolation scope this subscriber belongs to. */
  scope?: 'tenant' | 'platform';
  tenantId?: string;
  /** Resolved tenant display name when the product supplied one. */
  tenantName?: string;
  userId?: string;
  /** Compact, human-readable summary suitable for a single line. */
  label: string;
};

/** Ids are long UUIDs; the first segment is enough to recognise one at a glance. */
function shortenId(value?: string): string | undefined {
  if (!value) return undefined;
  return value.length > 12 ? `${value.slice(0, 8)}…` : value;
}

function readString(data: Record<string, unknown> | null | undefined, ...keys: string[]): string | undefined {
  if (!data) return undefined;
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

export function parseSubscriberIdentity(
  subscriberId: string,
  data?: Record<string, unknown> | null
): SubscriberIdentity {
  const tenantName = readString(data, 'tenantName', 'tenant_name', 'hrmsTenantName');

  // Anything that doesn't follow the convention is shown as-is — we must not mangle ids from
  // products that pick their own scheme.
  if (!subscriberId.startsWith('product:')) {
    return { structured: false, label: subscriberId, tenantName };
  }

  const parts = subscriberId.split(':');
  const product = parts[1];
  let scope: 'tenant' | 'platform' | undefined;
  let tenantId: string | undefined;
  let userId: string | undefined;

  for (let index = 2; index < parts.length; index += 1) {
    if (parts[index] === 'tenant' && parts[index + 1]) {
      scope = 'tenant';
      tenantId = parts[index + 1];
      index += 1;
    } else if (parts[index] === 'platform') {
      scope = 'platform';
    } else if (parts[index] === 'user' && parts[index + 1]) {
      userId = parts[index + 1];
      index += 1;
    }
  }

  if (!product || (!scope && !userId)) {
    return { structured: false, label: subscriberId, tenantName };
  }

  const scopeLabel =
    scope === 'platform'
      ? 'Platform'
      : tenantName || (tenantId ? `Tenant ${shortenId(tenantId)}` : 'Tenant');

  return {
    structured: true,
    product,
    scope,
    tenantId,
    tenantName,
    userId,
    label: [product, scopeLabel, userId ? `user ${shortenId(userId)}` : undefined]
      .filter(Boolean)
      .join(' · '),
  };
}
